import { useState, useEffect, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { jwtDecode } from "jwt-decode";

import MDBox from "components/MDBox";
import "../node_modules/bootstrap/dist/css/bootstrap.css";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import SignIn from "layouts/authentication/sign-in";
import HRSignIn from "layouts/authentication/hr-sign-in";
import ForgotPassword from "layouts/authentication/forgotPassword/forgotpasswordStep1";
import ResetPassword from "layouts/authentication/forgotPassword/forgotPasswordStep2";
import BlogPost from "./layouts/blog/data/blogPost";
import JobPost from "./layouts/jobPage/section/postJob";
import JobDepartment from "./layouts/jobPage/viewJobs/data/departments";
import BlogDetails from "./layouts/viewBlogs/data/viewBlogDetails";
import JobDetails from "./layouts/jobPage/viewJobs/data/viewBlogDetails";
import PaylipTab from "./layouts/hrSlipPort/index";
import NotFound from "not-found/no-found";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import brandWhite from "assets/images/logo.png";
import brandDark from "assets/images/logoLight.png";
import {
  getAuthState,
  clearAuthSession,
  getDefaultRouteForScope,
  filterRoutesByScope,
} from "utils/auth";

const getProtectedExtras = (scope) => {
  if (scope === "hr") {
    return [{ path: "/pay-slip", element: <PaylipTab /> }];
  }

  return [
    { path: "/blog-details", element: <BlogDetails /> },
    { path: "/job-details", element: <JobDetails /> },
    { path: "/post-blog", element: <BlogPost /> },
    { path: "/post-job", element: <JobPost /> },
    { path: "/job-department", element: <JobDepartment /> },
    { path: "/pay-slip", element: <PaylipTab /> },
  ];
};

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState(getAuthState());
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = location;

  const isPublicRoute =
    pathname === "/" ||
    pathname === "/hr-login" ||
    pathname === "/forgotPassword" ||
    pathname.startsWith("/resetPassword");

  const filteredRoutes = useMemo(
    () => filterRoutesByScope(routes, authState.scope),
    [authState.scope]
  );

  const hasToken = Boolean(authState.token && authState.user);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    const syncAuthState = () => setAuthState(getAuthState());
    syncAuthState();
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("focus", syncAuthState);

    return () => {
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("focus", syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!authState.token) {
      if (!isPublicRoute) {
        navigate("/");
      }
      return;
    }

    try {
      const { exp } = jwtDecode(authState.token);
      if (exp < Date.now() / 1000) {
        clearAuthSession();
        setAuthState(getAuthState());
        navigate("/");
        window.location.href = "/";
        toast.info("Your session has expired. Please log in again to continue.");
      }
    } catch (error) {
      console.error("Invalid token:", error);
      clearAuthSession();
      setAuthState(getAuthState());
      if (!isPublicRoute) {
        navigate("/");
      }
    }
  }, [authState.token, isPublicRoute, navigate]);

  useEffect(() => {
    if (!hasToken) return;

    if (pathname === "/" || pathname === "/hr-login") {
      const targetRoute = getDefaultRouteForScope(authState.scope);
      navigate(targetRoute);
    }
  }, [authState.scope, hasToken, navigate, pathname]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.component) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <ToastContainer />
      {hasToken ? (
        <>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                routes={filteredRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              <Configurator />
              {configsButton}
            </>
          )}

          <Routes>
            {getProtectedExtras(authState.scope).map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            {getRoutes(filteredRoutes)}
            <Route path="/" element={<Navigate to={getDefaultRouteForScope(authState.scope)} replace />} />
            <Route path="/hr-login" element={<Navigate to={getDefaultRouteForScope(authState.scope)} replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </>
      ) : (
        <Routes>
          <Route path="/" element={<SignIn />} />
          <Route path="/hr-login" element={<HRSignIn />} />
          <Route path="/forgotPassword" element={<ForgotPassword />} />
          <Route path="/resetPassword/:token" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </ThemeProvider>
  );
}
